import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  Input,
  Modal,
  ModalDialog,
  Option,
  Select,
  Stack,
  Textarea,
  Typography,
} from '@mui/joy';
import type { Document, DocumentWithLines } from '../../docs/types/documents.types';
import { CIS_BAND_LABELS, type CisBand, type DirectoryAccount } from '../accounts.model';
import { AccountFormModal } from './AccountsTab';
import {
  ARRANGEMENT_INVOICE_LABELS,
  ARRANGEMENT_TYPE_LABELS,
  VAT_TREATMENT_LABELS,
  arrangementEditLocked,
  availableInvoiceKinds,
  nextBillingPeriod,
  chargesVat,
  formatGbp,
  grossFromNet,
  invoiceCis,
  netFromGross,
  planArrangementInvoice,
  validateArrangementEdit,
  validateArrangementInput,
  type ArrangementInvoiceKind,
  type ArrangementType,
  type BillingArrangement,
  type BillingArrangementInput,
  type VatTreatment,
} from '../arrangements.model';
import { arrangementsService } from '../arrangements.service';
import { BILLING_FREQUENCY_LABELS, periodLabel, type BillingFrequency } from '../recurrence.model';

const VAT_TREATMENTS = Object.keys(VAT_TREATMENT_LABELS) as VatTreatment[];
const ARRANGEMENT_TYPES = Object.keys(ARRANGEMENT_TYPE_LABELS) as ArrangementType[];
const BILLING_FREQUENCIES = Object.keys(BILLING_FREQUENCY_LABELS) as BillingFrequency[];
const CIS_BANDS = Object.keys(CIS_BAND_LABELS) as CisBand[];

function optionalNumber(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : NaN;
}

export function ledgerInvoices(rows: Array<Record<string, any>>) {
  return rows.map((row) => ({
    id: row.id,
    role: row.invoice_role ?? row.invoiceRole ?? null,
    status: row.status,
    grossTotal: Number(row.gross_total ?? row.grossTotal ?? 0),
  }));
}

/** Choose the account being invoiced, with a shortcut to add a new one without leaving the form. */
export function AccountPicker({
  accounts,
  value,
  onChange,
  onAccountCreated,
  disabled,
  helper,
}: {
  accounts: DirectoryAccount[];
  value: string;
  onChange: (accountId: string) => void;
  onAccountCreated?: (account: DirectoryAccount) => void;
  disabled?: boolean;
  helper?: string | null;
}) {
  const [adding, setAdding] = useState(false);
  const choices = accounts.filter((account) => account.isActive || account.id === value);

  return (
    <>
      <FormControl required disabled={disabled}>
        <FormLabel>Account</FormLabel>
        <Stack direction="row" spacing={1}>
          <Select
            value={value || null}
            placeholder={choices.length ? 'Choose an account' : 'No accounts yet'}
            onChange={(_, next) => onChange(String(next || ''))}
            sx={{ flex: 1, minWidth: 0 }}
          >
            {choices.map((account) => <Option key={account.id} value={account.id}>{account.companyName}</Option>)}
          </Select>
          {!disabled && (
            <Button variant="outlined" color="neutral" onClick={() => setAdding(true)} sx={{ whiteSpace: 'nowrap' }}>
              + New account
            </Button>
          )}
        </Stack>
        {helper && <FormHelperText>{helper}</FormHelperText>}
      </FormControl>
      <AccountFormModal
        account={null}
        open={adding}
        onClose={() => setAdding(false)}
        onSaved={(account) => {
          setAdding(false);
          onAccountCreated?.(account);
          onChange(account.id);
        }}
      />
    </>
  );
}

/** The nature of the arrangement: partner trade, tenant, supplier … */
export function ArrangementTypeSelect({
  value,
  onChange,
}: {
  value: ArrangementType;
  onChange: (type: ArrangementType) => void;
}) {
  return (
    <FormControl required>
      <FormLabel>Type</FormLabel>
      <Select value={value} onChange={(_, next) => next && onChange(next as ArrangementType)}>
        {ARRANGEMENT_TYPES.map((type) => <Option key={type} value={type}>{ARRANGEMENT_TYPE_LABELS[type]}</Option>)}
      </Select>
    </FormControl>
  );
}

/** Whether the customer deducts CIS from our invoices, and at Keystone's rate. */
export function CisSelect({
  value,
  onChange,
  disabled,
  helper,
}: {
  value: CisBand;
  onChange: (band: CisBand) => void;
  disabled?: boolean;
  helper?: string | null;
}) {
  return (
    <FormControl disabled={disabled}>
      <FormLabel>CIS</FormLabel>
      <Select value={value} onChange={(_, next) => next && onChange(next as CisBand)}>
        {CIS_BANDS.map((band) => <Option key={band} value={band}>{CIS_BAND_LABELS[band]}</Option>)}
      </Select>
      <FormHelperText>{helper || 'For CIS work: Keystone’s own rate, which the customer deducts from labour.'}</FormHelperText>
    </FormControl>
  );
}

/** Creates an arrangement, or edits one when `arrangement` is passed. */
export function ArrangementFormModal({
  open,
  arrangement,
  invoices,
  accounts,
  onClose,
  onSaved,
  onAccountCreated,
}: {
  open: boolean;
  arrangement?: BillingArrangement | null;
  invoices?: Array<Record<string, any>>;
  accounts: DirectoryAccount[];
  onClose: () => void;
  onSaved: (arrangement: BillingArrangement) => void;
  onAccountCreated?: (account: DirectoryAccount) => void;
}) {
  const editing = !!arrangement;
  const ledger = useMemo(() => ledgerInvoices(invoices || []), [invoices]);
  const locked = editing && arrangementEditLocked(ledger);
  const [accountId, setAccountId] = useState('');
  const [arrangementType, setArrangementType] = useState<ArrangementType>('partner_trade');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [agreedNet, setAgreedNet] = useState('');
  const [vatTreatment, setVatTreatment] = useState<VatTreatment>('standard');
  const [vatPercent, setVatPercent] = useState('20');
  const [depositPercent, setDepositPercent] = useState('');
  const [terms, setTerms] = useState('7');
  const [frequency, setFrequency] = useState<BillingFrequency>('none');
  const [recurringNet, setRecurringNet] = useState('');
  const [firstPeriodStart, setFirstPeriodStart] = useState('');
  const [endsOn, setEndsOn] = useState('');
  const [cisBand, setCisBand] = useState<CisBand>('none');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const recurring = frequency !== 'none';
  const scheduleLocked = !!arrangement && arrangement.nextPeriodIndex > 0;

  useEffect(() => {
    if (!open) return;
    const percent = (ratio: number) => String(Math.round(ratio * 100000) / 1000);
    setFrequency(arrangement?.frequency || 'none');
    setRecurringNet(arrangement?.recurringNet != null ? String(arrangement.recurringNet) : '');
    setFirstPeriodStart(arrangement?.firstPeriodStart || '');
    setEndsOn(arrangement?.endsOn || '');
    setCisBand(arrangement?.cisBand || 'none');
    setAccountId(arrangement?.accountId || '');
    setArrangementType(arrangement?.arrangementType || 'partner_trade');
    setTitle(arrangement?.title || '');
    setDescription(arrangement?.description || '');
    setAgreedNet(arrangement?.agreedGross != null
      ? String(netFromGross(arrangement.agreedGross, arrangement.vatTreatment, arrangement.vatPct))
      : '');
    setVatTreatment(arrangement?.vatTreatment || 'standard');
    setVatPercent(arrangement ? percent(arrangement.vatPct) : '20');
    setDepositPercent(arrangement?.depositPercentage != null ? percent(arrangement.depositPercentage) : '');
    setTerms(String(arrangement?.paymentTermsInDays ?? 7));
    setError(null);
  }, [open, arrangement]);

  const save = async () => {
    const agreed = optionalNumber(agreedNet);
    const deposit = optionalNumber(depositPercent);
    const vat = optionalNumber(vatPercent);
    const days = optionalNumber(terms);
    const perPeriod = optionalNumber(recurringNet);
    if ([agreed, deposit, vat, days, perPeriod].some((value) => Number.isNaN(value))) {
      setError('Amounts and percentages must be numbers.');
      return;
    }
    const input: BillingArrangementInput = {
      accountId,
      arrangementType,
      title,
      description,
      agreedNet: recurring ? null : agreed,
      vatTreatment,
      vatPct: (vat ?? 20) / 100,
      depositPercentage: recurring || deposit == null ? null : deposit / 100,
      paymentTermsInDays: days ?? 7,
      frequency,
      recurringNet: recurring ? perPeriod : null,
      firstPeriodStart: recurring ? firstPeriodStart || null : null,
      endsOn: recurring ? endsOn || null : null,
      cisBand,
    };
    const problem = arrangement
      ? validateArrangementEdit(arrangement, input, ledger)
      : validateArrangementInput(input);
    if (problem) {
      setError(problem);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      onSaved(arrangement
        ? await arrangementsService.update(arrangement.id, input)
        : await arrangementsService.create(input));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save the arrangement');
    } finally {
      setSaving(false);
    }
  };

  const lockedHelp = locked ? 'Fixed once invoices have been raised.' : null;

  return (
    <Modal open={open} onClose={() => !saving && onClose()}>
      <ModalDialog sx={{ width: { xs: '100%', sm: 600 }, maxHeight: '90vh', overflow: 'auto' }}>
        <DialogTitle>{arrangement ? `Edit ${arrangement.lifecycleRef}` : 'New arrangement'}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            <Typography level="body-sm" textColor="neutral.600">
              {arrangement
                ? 'Changes apply to invoices raised from now on. Existing invoices keep what they were issued with.'
                : 'Mints the next KSU reference. Every invoice on this arrangement is numbered from it.'}
            </Typography>
            <AccountPicker
              accounts={accounts}
              value={accountId}
              onChange={setAccountId}
              onAccountCreated={onAccountCreated}
              disabled={locked}
              helper={lockedHelp}
            />
            <ArrangementTypeSelect value={arrangementType} onChange={setArrangementType} />
            <FormControl required>
              <FormLabel>Title</FormLabel>
              <Input value={title} onChange={(event) => setTitle(event.target.value)} placeholder="e.g. Groundworks at 12 Mill Lane, or Unit 3 rent" />
            </FormControl>
            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea minRows={2} value={description} onChange={(event) => setDescription(event.target.value)} />
              <FormHelperText>Printed in the notes of every invoice on this arrangement.</FormHelperText>
            </FormControl>
            <Typography level="title-sm" sx={{ pt: 0.5 }}>Billing</Typography>
            <Grid container spacing={1.5}>
              <Grid xs={12} sm={6}>
                <FormControl disabled={scheduleLocked || (locked && !recurring)}>
                  <FormLabel>Frequency</FormLabel>
                  <Select value={frequency} onChange={(_, value) => value && setFrequency(value as BillingFrequency)}>
                    {BILLING_FREQUENCIES.map((option) => <Option key={option} value={option}>{BILLING_FREQUENCY_LABELS[option]}</Option>)}
                  </Select>
                  <FormHelperText>
                    {scheduleLocked
                      ? 'Fixed once a period has been invoiced.'
                      : recurring ? 'One invoice per period, raised in advance.' : 'Raise invoices when you need them.'}
                  </FormHelperText>
                </FormControl>
              </Grid>
              {recurring ? (
                <>
                  <Grid xs={12} sm={6}>
                    <FormControl required>
                      <FormLabel>Amount per period (ex VAT)</FormLabel>
                      <Input type="number" value={recurringNet} onChange={(event) => setRecurringNet(event.target.value)} startDecorator="£" />
                      <FormHelperText>Changes apply to periods not yet invoiced.</FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid xs={12} sm={6}>
                    <FormControl required disabled={scheduleLocked}>
                      <FormLabel>First period starts</FormLabel>
                      <Input type="date" value={firstPeriodStart} onChange={(event) => setFirstPeriodStart(event.target.value)} />
                    </FormControl>
                  </Grid>
                  <Grid xs={12} sm={6}>
                    <FormControl>
                      <FormLabel>Ends</FormLabel>
                      <Input type="date" value={endsOn} onChange={(event) => setEndsOn(event.target.value)} />
                      <FormHelperText>Optional. No periods start after this date.</FormHelperText>
                    </FormControl>
                  </Grid>
                </>
              ) : (
                <>
                  <Grid xs={12} sm={6}>
                    <FormControl>
                      <FormLabel>Agreed amount (ex VAT)</FormLabel>
                      <Input type="number" value={agreedNet} onChange={(event) => setAgreedNet(event.target.value)} startDecorator="£" />
                      <FormHelperText>
                        {locked ? 'Can go up, or down to what has been invoiced.' : 'Leave blank for open-ended.'}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid xs={12} sm={6}>
                    <FormControl disabled={locked || !agreedNet.trim()}>
                      <FormLabel>Deposit</FormLabel>
                      <Input type="number" value={depositPercent} onChange={(event) => setDepositPercent(event.target.value)} endDecorator="%" />
                      <FormHelperText>{lockedHelp || 'Blank means no deposit.'}</FormHelperText>
                    </FormControl>
                  </Grid>
                </>
              )}
            </Grid>
            <Typography level="title-sm" sx={{ pt: 0.5 }}>VAT, CIS and terms</Typography>
            <Grid container spacing={1.5}>
              <Grid xs={12} sm={6}>
                <FormControl disabled={locked}>
                  <FormLabel>VAT</FormLabel>
                  <Select value={vatTreatment} onChange={(_, value) => value && setVatTreatment(value as VatTreatment)}>
                    {VAT_TREATMENTS.map((treatment) => <Option key={treatment} value={treatment}>{VAT_TREATMENT_LABELS[treatment]}</Option>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid xs={6} sm={3}>
                <FormControl disabled={locked}>
                  <FormLabel>VAT rate</FormLabel>
                  <Input type="number" value={vatPercent} onChange={(event) => setVatPercent(event.target.value)} endDecorator="%" />
                </FormControl>
              </Grid>
              <Grid xs={6} sm={3}>
                <FormControl>
                  <FormLabel>Terms</FormLabel>
                  <Input type="number" value={terms} onChange={(event) => setTerms(event.target.value)} endDecorator="days" />
                </FormControl>
              </Grid>
              <Grid xs={12}>
                <CisSelect value={cisBand} onChange={setCisBand} disabled={locked} helper={locked ? lockedHelp : null} />
              </Grid>
            </Grid>
            {vatTreatment === 'reverse_charge' && (
              <Alert color="neutral" variant="soft">
                No VAT is charged. Each invoice notes that the customer pays the VAT to HMRC, and FreeAgent records it as reverse charge.
              </Alert>
            )}
            {error && <Alert color="danger">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={save} loading={saving}>{arrangement ? 'Save' : 'Create arrangement'}</Button>
          <Button variant="plain" color="neutral" onClick={onClose} disabled={saving}>Cancel</Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
}

/** One step for a single invoice. Creates and closes its own arrangement behind the scenes. */
export function NewInvoiceModal({
  open,
  accounts,
  onClose,
  onCreated,
  onAccountCreated,
}: {
  open: boolean;
  accounts: DirectoryAccount[];
  onClose: () => void;
  onCreated: (result: { arrangement: BillingArrangement; invoice: DocumentWithLines }) => void;
  onAccountCreated?: (account: DirectoryAccount) => void;
}) {
  const [accountId, setAccountId] = useState('');
  const [arrangementType, setArrangementType] = useState<ArrangementType>('partner_trade');
  const [title, setTitle] = useState('');
  const [lineName, setLineName] = useState('');
  const [amount, setAmount] = useState('');
  const [vatTreatment, setVatTreatment] = useState<VatTreatment>('standard');
  const [vatPercent, setVatPercent] = useState('20');
  const [terms, setTerms] = useState('7');
  const [cisBand, setCisBand] = useState<CisBand>('none');
  const [cisLabour, setCisLabour] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setCisBand('none');
    setCisLabour('');
    setAccountId('');
    setArrangementType('partner_trade');
    setTitle('');
    setLineName('');
    setAmount('');
    setVatTreatment('standard');
    setVatPercent('20');
    setTerms('7');
    setError(null);
  }, [open]);

  const net = Number(amount);
  const vatPct = (Number(vatPercent) || 0) / 100;
  const gross = net > 0 ? grossFromNet(net, vatTreatment, vatPct) : null;
  const cisPreview = (() => {
    if (gross == null) return null;
    try {
      return invoiceCis({ band: cisBand, amountNet: net, amountGross: gross, labourNet: cisLabour.trim() ? Number(cisLabour) : null });
    } catch {
      return null;
    }
  })();

  const save = async () => {
    if (!accountId) return setError('Choose the account to invoice.');
    if (!title.trim()) return setError('Say what the invoice is for.');
    if (!(net > 0)) return setError('Enter an amount greater than zero.');
    const days = Number(terms);
    if (!(Number.isInteger(days) && days >= 0)) return setError('Payment terms must be a whole number of days.');
    setSaving(true);
    setError(null);
    try {
      onCreated(await arrangementsService.createOneOffInvoice({
        accountId,
        arrangementType,
        title,
        amountNet: net,
        lineName: lineName || title,
        vatTreatment,
        vatPct,
        paymentTermsInDays: days,
        cisBand,
        cisLabourNet: cisBand !== 'none' && cisLabour.trim() ? Number(cisLabour) : null,
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create the invoice');
    } finally {
      setSaving(false);
    }
    return undefined;
  };


  return (
    <Modal open={open} onClose={() => !saving && onClose()}>
      <ModalDialog sx={{ width: { xs: '100%', sm: 560 }, maxHeight: '90vh', overflow: 'auto' }}>
        <DialogTitle>New invoice</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            <Typography level="body-sm" textColor="neutral.600">
              For a single invoice. For deposit, interim and balance invoices against an agreed amount, use New arrangement.
            </Typography>
            <AccountPicker
              accounts={accounts}
              value={accountId}
              onChange={setAccountId}
              onAccountCreated={onAccountCreated}
            />
            <ArrangementTypeSelect value={arrangementType} onChange={setArrangementType} />
            <FormControl required>
              <FormLabel>What it’s for</FormLabel>
              <Input value={title} onChange={(event) => setTitle(event.target.value)} placeholder="e.g. Skip recharge, October" />
            </FormControl>
            <FormControl>
              <FormLabel>Line description</FormLabel>
              <Input value={lineName} onChange={(event) => setLineName(event.target.value)} placeholder={title || 'Same as above'} />
            </FormControl>
            <Grid container spacing={1.5}>
              <Grid xs={12} sm={6}>
                <FormControl required>
                  <FormLabel>Amount (ex VAT)</FormLabel>
                  <Input type="number" value={amount} onChange={(event) => setAmount(event.target.value)} startDecorator="£" />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6}>
                <FormControl>
                  <FormLabel>VAT</FormLabel>
                  <Select value={vatTreatment} onChange={(_, value) => value && setVatTreatment(value as VatTreatment)}>
                    {VAT_TREATMENTS.map((treatment) => <Option key={treatment} value={treatment}>{VAT_TREATMENT_LABELS[treatment]}</Option>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid xs={6}>
                <FormControl disabled={!chargesVat(vatTreatment)}>
                  <FormLabel>VAT rate</FormLabel>
                  <Input type="number" value={vatPercent} onChange={(event) => setVatPercent(event.target.value)} endDecorator="%" />
                </FormControl>
              </Grid>
              <Grid xs={6}>
                <FormControl>
                  <FormLabel>Terms</FormLabel>
                  <Input type="number" value={terms} onChange={(event) => setTerms(event.target.value)} endDecorator="days" />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={cisBand === 'none' ? 12 : 6}>
                <CisSelect value={cisBand} onChange={setCisBand} />
              </Grid>
              {cisBand !== 'none' && (
                <Grid xs={12} sm={6}>
                  <FormControl>
                    <FormLabel>Of which labour (ex VAT)</FormLabel>
                    <Input type="number" value={cisLabour} onChange={(event) => setCisLabour(event.target.value)} startDecorator="£" placeholder={amount || ''} />
                    <FormHelperText>Blank means it is all labour.</FormHelperText>
                  </FormControl>
                </Grid>
              )}
            </Grid>
            {gross != null && (
              <Typography level="body-sm">
                {formatGbp(net)} net
                {chargesVat(vatTreatment) ? ` + ${formatGbp(gross - net)} VAT = ${formatGbp(gross)}` : ` · ${VAT_TREATMENT_LABELS[vatTreatment]}, no VAT charged`}
                {cisPreview && cisPreview.deduction > 0 ? ` · less CIS ${formatGbp(cisPreview.deduction)} = ${formatGbp(cisPreview.payableAfterCis)} payable` : ''}
              </Typography>
            )}
            {error && <Alert color="danger">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={save} loading={saving}>Create draft invoice</Button>
          <Button variant="plain" color="neutral" onClick={onClose} disabled={saving}>Cancel</Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
}

export function IssueInvoiceCard({
  arrangement,
  invoices,
  onIssued,
}: {
  arrangement: BillingArrangement;
  invoices: Array<Record<string, any>>;
  onIssued: (invoice: Document) => void;
}) {
  const ledger = useMemo(() => ledgerInvoices(invoices), [invoices]);
  const kinds = useMemo(() => availableInvoiceKinds(arrangement, ledger), [arrangement, ledger]);
  const [kind, setKind] = useState<ArrangementInvoiceKind | null>(null);
  const [amount, setAmount] = useState('');
  const [lineName, setLineName] = useState('');
  const [cisLabour, setCisLabour] = useState('');
  const [issuing, setIssuing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setKind(kinds[0] ?? null);
    setAmount('');
    setLineName('');
    setCisLabour('');
    setError(null);
  }, [arrangement.id, kinds]);

  const needsAmount = kind === 'interim' || kind === 'one_off';
  const nextPeriod = useMemo(() => nextBillingPeriod(arrangement), [arrangement]);
  const kindLabel = (option: ArrangementInvoiceKind) => (
    option === 'recurring' && nextPeriod ? `Next period (${periodLabel(nextPeriod)})` : ARRANGEMENT_INVOICE_LABELS[option]
  );
  const preview = useMemo(() => {
    if (!kind) return null;
    try {
      return {
        plan: planArrangementInvoice({
          arrangement,
          invoices: ledger,
          kind,
          amountNet: Number(amount),
          lineName,
          cisLabourNet: cisLabour.trim() ? Number(cisLabour) : null,
        }),
        problem: null,
      };
    } catch (err) {
      return { plan: null, problem: err instanceof Error ? err.message : 'Cannot plan this invoice' };
    }
  }, [arrangement, ledger, kind, amount, lineName, cisLabour]);

  if (kinds.length === 0) {
    return (
      <Alert color="neutral" variant="soft">
        {arrangement.status === 'closed' ? 'This arrangement is closed. Reopen it to raise more invoices.' : 'The agreed amount has been fully invoiced.'}
      </Alert>
    );
  }

  const issue = async () => {
    if (!kind) return;
    setIssuing(true);
    setError(null);
    try {
      const invoice = await arrangementsService.issueInvoice({
        arrangementId: arrangement.id,
        kind,
        amountNet: needsAmount ? Number(amount) : null,
        lineName,
        cisLabourNet: cisLabour.trim() ? Number(cisLabour) : null,
      });
      onIssued(invoice);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create the invoice');
    } finally {
      setIssuing(false);
    }
  };

  return (
    <Card variant="outlined">
      <Stack spacing={1.25}>
        <Typography level="title-md">Raise an invoice</Typography>
        <Grid container spacing={1.5}>
          <Grid xs={12} sm={kind === 'recurring' ? 6 : 4}>
            <FormControl>
              <FormLabel>Invoice for</FormLabel>
              <Select value={kind} onChange={(_, value) => value && setKind(value as ArrangementInvoiceKind)}>
                {kinds.map((option) => <Option key={option} value={option}>{kindLabel(option)}</Option>)}
              </Select>
            </FormControl>
          </Grid>
          {needsAmount && (
            <Grid xs={12} sm={4}>
              <FormControl>
                <FormLabel>Amount (ex VAT)</FormLabel>
                <Input type="number" value={amount} onChange={(event) => setAmount(event.target.value)} startDecorator="£" />
              </FormControl>
            </Grid>
          )}
          <Grid xs={12} sm={needsAmount ? 4 : kind === 'recurring' ? 6 : 8}>
            <FormControl>
              <FormLabel>Line description</FormLabel>
              <Input
                value={lineName}
                onChange={(event) => setLineName(event.target.value)}
                placeholder={kind === 'recurring' || kind === 'one_off' ? arrangement.title : kind ? ARRANGEMENT_INVOICE_LABELS[kind] : ''}
              />
              {kind === 'recurring' && <FormHelperText>The period dates are added automatically.</FormHelperText>}
            </FormControl>
          </Grid>
          {arrangement.cisBand !== 'none' && (
            <Grid xs={12} sm={6}>
              <FormControl>
                <FormLabel>Of which labour (ex VAT)</FormLabel>
                <Input
                  type="number"
                  value={cisLabour}
                  onChange={(event) => setCisLabour(event.target.value)}
                  startDecorator="£"
                  placeholder={preview?.plan ? String(preview.plan.amountNet) : ''}
                />
                <FormHelperText>CIS is deducted from labour only. Blank means it is all labour.</FormHelperText>
              </FormControl>
            </Grid>
          )}
        </Grid>
        {preview?.plan && (
          <Typography level="body-sm">
            {formatGbp(preview.plan.amountNet)} net
            {preview.plan.vatEnabled ? ` + ${formatGbp(preview.plan.vatAmount)} VAT = ${formatGbp(preview.plan.amountGross)}` : ` · ${VAT_TREATMENT_LABELS[arrangement.vatTreatment]}, no VAT charged`}
            {preview.plan.cis && preview.plan.cis.deduction > 0
              ? ` · less CIS ${formatGbp(preview.plan.cis.deduction)} = ${formatGbp(preview.plan.cis.payableAfterCis)} payable`
              : ''}
          </Typography>
        )}
        {preview?.problem && (needsAmount ? amount.trim() !== '' : true) && (
          <Typography level="body-sm" color="warning">{preview.problem}</Typography>
        )}
        {error && <Alert color="danger">{error}</Alert>}
        <Box>
          <Button onClick={issue} loading={issuing} disabled={!preview?.plan}>
            {kind === 'recurring' && preview?.plan?.period ? `Raise invoice for ${periodLabel(preview.plan.period)}` : 'Create draft invoice'}
          </Button>
        </Box>
      </Stack>
    </Card>
  );
}
